/** Styled language choices submit through Horizon's existing localization form. */
class SuitShopLanguageMenu extends HTMLElement {
  #controller;
  #button;
  #panel;
  #select;
  #options;

  async connectedCallback() {
    await customElements.whenDefined('localization-form-component');
    if (!this.isConnected || this.#controller) return;
    this.#button = this.querySelector('.suit-shop-language-trigger');
    this.#panel = this.querySelector('.suit-shop-language-menu-panel');
    this.#select = this.querySelector('select[name="language_code"]');
    this.#options = [...this.querySelectorAll('[data-language]')];
    if (!this.#button || !this.#panel || !this.#select || !this.#options.length) return;
    // Older browsers keep the functional native select instead of a clipped menu.
    if (typeof this.#panel.showPopover !== 'function') return;
    this.#controller = new AbortController();
    const listener = { signal: this.#controller.signal };
    this.#select.hidden = true;
    this.#button.hidden = false;
    this.dataset.enhanced = 'true';
    this.#button.addEventListener('click', () => this.#isOpen() ? this.#close() : this.#open(), listener);
    this.addEventListener('keydown', (event) => this.#handleKey(event), listener);
    // Keep focus in the menu until click completes, including touch selection.
    this.#panel.addEventListener('pointerdown', (event) => {
      if (event.target.closest('[data-language]')) event.preventDefault();
    }, listener);
    this.#panel.addEventListener('click', (event) => {
      const option = event.target.closest('[data-language]');
      if (!option) return;
      this.#close(true);
      if (option.dataset.language === this.#select.value) return;
      this.#select.value = option.dataset.language;
      this.#select.dispatchEvent(new Event('change', { bubbles: true }));
    }, listener);
    this.#panel.addEventListener('toggle', (event) => {
      this.#button.setAttribute('aria-expanded', String(event.newState === 'open'));
    }, listener);
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.#close();
    }, listener);
    this.addEventListener('focusout', () => {
      queueMicrotask(() => { if (!this.contains(document.activeElement)) this.#close(); });
    }, listener);
    window.addEventListener('resize', () => this.#close(), listener);
  }

  disconnectedCallback() {
    this.#close();
    this.#controller?.abort();
    this.#controller = null;
  }

  #isOpen() { return this.#button?.getAttribute('aria-expanded') === 'true'; }

  #open(last = false) {
    const rect = this.#button.getBoundingClientRect();
    const width = Math.min(176, window.innerWidth - 24);
    const rtl = getComputedStyle(this).direction === 'rtl';
    const left = Math.max(12, Math.min(rtl ? rect.left : rect.right - width, window.innerWidth - width - 12));
    this.#panel.style.width = `${width}px`;
    this.#panel.style.left = `${left}px`;
    this.#panel.hidden = false;
    this.#panel.showPopover();
    const height = this.#panel.getBoundingClientRect().height;
    this.#panel.style.top = `${Math.max(12, Math.min(rect.bottom + 8, window.innerHeight - height - 12))}px`;
    this.#button.setAttribute('aria-expanded', 'true');
    const active = this.#options.find((option) => option.getAttribute('aria-checked') === 'true');
    (last ? this.#options.at(-1) : active || this.#options[0]).focus({ preventScroll: true });
  }

  #close(focus = false) {
    if (!this.#panel || !this.#button) return;
    if (typeof this.#panel.hidePopover === 'function' && this.#panel.matches(':popover-open')) this.#panel.hidePopover();
    this.#panel.hidden = true;
    this.#button.setAttribute('aria-expanded', 'false');
    if (focus) this.#button.focus({ preventScroll: true });
  }

  #handleKey(event) {
    if (event.key === 'Escape' && this.#isOpen()) {
      event.preventDefault();
      event.stopPropagation();
      this.#close(true);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (!this.#isOpen()) { this.#open(event.key === 'ArrowUp' || event.key === 'End'); return; }
    const index = this.#options.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.#options.length - 1
      : (index + (event.key === 'ArrowUp' ? -1 : 1) + this.#options.length) % this.#options.length;
    this.#options[next].focus({ preventScroll: true });
  }
}

if (!customElements.get('suit-shop-language-menu')) {
  customElements.define('suit-shop-language-menu', SuitShopLanguageMenu);
}
